let isProcessingCheckout = false;

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function setOrderComplete() {
  const firstOrderOriginalTime = localStorage.getItem('firstOrderOriginalTime');
  if (!firstOrderOriginalTime) {
    localStorage.setItem('firstOrderOriginalTime', Date.now().toString());
  }
  localStorage.setItem('isFirstOrder', 'false');
  localStorage.setItem('firstOrderTime', Date.now().toString());
}

function proceedToCheckout(checkoutUrl) {
  const url = checkoutUrl || (window.getCheckoutUrl && window.getCheckoutUrl()) || '/checkout';
  window.location.href = url;
}

async function addFirstOrderGift(url = '/cart', opts = {}) {
  const finish = () => {
    if (opts.noRedirect && opts.onComplete) opts.onComplete();
    else if (window.useCartDrawer && window.useCartDrawer()) {
      if (typeof window.cart_open === 'function') window.cart_open();
      if (typeof window.cart_build === 'function') window.cart_build();
    } else {
      window.location.href = url;
    }
  };

  if (opts.freeGiftManuallyRemoved) {
    localStorage.setItem('FREE_GIFT_MANUALLY_REMOVED', '1');
    finish();
    return;
  }

  const freeGiftManager = window.freeGiftManager;
  if (!freeGiftManager?.enabled) {
    finish();
    return;
  }

  if (freeGiftManager.customerHasOrders) {
    finish();
    return;
  }

  const freeGiftVariantId = freeGiftManager.freeGiftVariantId;
  const promoGiftManager = window.promoGiftManager;
  const log = window.log;

  try {
    const cart = await GiftCart.fetchCart();
    if (!cart || cart.items.length === 0) {
      finish();
      return;
    }

    const hasFreeGift = freeGiftManager.hasFreeGiftInCart(cart);
    const hasOtherProducts = cart.items.some(
      (item) => parseInt(item.variant_id, 10) !== parseInt(freeGiftVariantId, 10)
    );

    if (hasFreeGift && !freeGiftManager.hasEligibleProductInCart(cart)) {
      await GiftCart.applyCartUpdates({ [freeGiftVariantId]: 0 });
      localStorage.removeItem('ADD_FIRST_ORDER_GIFT_FREE');
      finish();
      return;
    }

    if (promoGiftManager?.enabled) {
      const strictItems = promoGiftManager.getStrictPromoGiftItems(cart);
      if (strictItems.length > 0 && !promoGiftManager.hasOtherProducts(cart)) {
        const promoUpdates = {};
        for (const vid of promoGiftManager.getStrictPromoGiftVariantIds()) {
          promoUpdates[vid] = 0;
        }
        await GiftCart.applyCartUpdates(promoUpdates);
        finish();
        return;
      }
    }

    let isFirstOrder = localStorage.getItem('isFirstOrder') !== 'false';
    if (!isFirstOrder) {
      const wasReset = freeGiftManager.checkAndResetFirstOrderStatus();
      isFirstOrder = localStorage.getItem('isFirstOrder') !== 'false';
      if (wasReset) {
        await freeGiftManager.addFreeGiftAfterReset();
        finish();
        return;
      }
      finish();
      return;
    }

    const manuallyRemoved = localStorage.getItem('FREE_GIFT_MANUALLY_REMOVED') === '1';
    const addFirstOrderGiftStorage = JSON.parse(localStorage.getItem('ADD_FIRST_ORDER_GIFT_FREE') || 'null');
    if (
      !manuallyRemoved &&
      addFirstOrderGiftStorage &&
      Date.now() - parseInt(addFirstOrderGiftStorage, 10) < 86400000
    ) {
      finish();
      return;
    }

    if (!hasFreeGift && hasOtherProducts) {
      if (
        !GiftCart.hasKachingBundleFreeGiftInCart(cart, freeGiftVariantId) &&
        freeGiftManager.hasEligibleProductInCart(cart)
      ) {
        await GiftCart.addToCart(freeGiftVariantId, 1);
        localStorage.setItem('ADD_FIRST_ORDER_GIFT_FREE', Date.now().toString());
        localStorage.removeItem('FREE_GIFT_MANUALLY_REMOVED');
        if (log) log.collapse({}, 'Free gift added successfully', 'success');
      } else if (log && GiftCart.hasKachingBundleFreeGiftInCart(cart, freeGiftVariantId)) {
        log.collapse({}, 'Kaching bundle free gift detected, skipping duplicate free gift', 'info');
      }
    }

    finish();
  } catch (error) {
    if (log) log.collapse(error, 'Error checking cart', 'error');
    finish();
  }
}

async function validateAndCleanCart(checkoutUrl) {
  if (isProcessingCheckout) return;
  isProcessingCheckout = true;

  const freeGiftManager = window.freeGiftManager;
  const promoGiftManager = window.promoGiftManager;
  const log = window.log;

  try {
    if (window.GiftCartSync?.busy) {
      await window.GiftCartSync.waitForIdle(2000);
    }

    let cart = await GiftCart.fetchCart();
    if (!cart) {
      isProcessingCheckout = false;
      setOrderComplete();
      proceedToCheckout(checkoutUrl);
      return;
    }

    if (freeGiftManager?.enabled) {
      freeGiftManager.updateCachedCart(cart);
      const freeResult = await freeGiftManager.enforceCart(cart);
      cart = freeResult.cart;
    }

    if (promoGiftManager?.enabled) {
      promoGiftManager.updateCachedCart(cart);
      const promoResult = await promoGiftManager.enforceCart(cart);
      cart = promoResult.cart;
    }

    const freeGiftVariantId = freeGiftManager?.freeGiftVariantId;
    if (!freeGiftVariantId) {
      isProcessingCheckout = false;
      setOrderComplete();
      proceedToCheckout(checkoutUrl);
      return;
    }

    const allGiftVariantIds = [freeGiftVariantId]
      .concat(promoGiftManager ? promoGiftManager.getStrictPromoGiftVariantIds() : [])
      .filter(Boolean);
    const isGiftVariant = (variantId) =>
      allGiftVariantIds.some((id) => parseInt(id, 10) === parseInt(variantId, 10));

    const freeGiftItems = cart.items.filter(
      (item) => parseInt(item.variant_id, 10) === parseInt(freeGiftVariantId, 10)
    );
    const hasFreeGift = freeGiftItems.length > 0;
    const hasOnlyGifts =
      cart.items.length > 0 && cart.items.every((item) => isGiftVariant(item.variant_id));
    const hasOtherProducts = cart.items.some((item) => !isGiftVariant(item.variant_id));

    if (hasOnlyGifts) {
      const updates = {};
      for (const id of allGiftVariantIds) updates[id] = 0;
      await GiftCart.applyCartUpdates(updates);
      localStorage.removeItem('ADD_FIRST_ORDER_GIFT_FREE');
      isProcessingCheckout = false;
      return;
    }

    if (hasFreeGift && hasOtherProducts) {
      if (
        GiftCart.hasKachingBundleFreeGiftInCart(cart, freeGiftVariantId) &&
        freeGiftItems.some((item) => !GiftCart.itemHasKachingBundleProperty(item))
      ) {
        await GiftCart.applyCartUpdates({ [freeGiftVariantId]: 0 });
        localStorage.removeItem('ADD_FIRST_ORDER_GIFT_FREE');
        isProcessingCheckout = false;
        setOrderComplete();
        proceedToCheckout(checkoutUrl);
        return;
      }

      if (!freeGiftManager.hasEligibleProductInCart(cart)) {
        await GiftCart.applyCartUpdates({ [freeGiftVariantId]: 0 });
        localStorage.removeItem('ADD_FIRST_ORDER_GIFT_FREE');
        isProcessingCheckout = false;
        setOrderComplete();
        proceedToCheckout(checkoutUrl);
        return;
      }
    }

    let isFirstOrder = localStorage.getItem('isFirstOrder') !== 'false';
    if (!isFirstOrder) {
      freeGiftManager.checkAndResetFirstOrderStatus();
      isFirstOrder = localStorage.getItem('isFirstOrder') !== 'false';
    }

    if (!hasFreeGift && hasOtherProducts && !freeGiftManager.customerHasOrders && isFirstOrder) {
      const manuallyRemoved = localStorage.getItem('FREE_GIFT_MANUALLY_REMOVED') === '1';
      if (manuallyRemoved) {
        localStorage.removeItem('FREE_GIFT_MANUALLY_REMOVED');
        isProcessingCheckout = false;
        setOrderComplete();
        proceedToCheckout(checkoutUrl);
        return;
      }

      const lastFirstOrderGift = JSON.parse(localStorage.getItem('ADD_FIRST_ORDER_GIFT_FREE') || 'null');
      const shouldAddFirstOrderGift =
        !lastFirstOrderGift || Date.now() - parseInt(lastFirstOrderGift, 10) > 86400000;

      if (
        shouldAddFirstOrderGift &&
        !GiftCart.hasKachingBundleFreeGiftInCart(cart, freeGiftVariantId) &&
        freeGiftManager.hasEligibleProductInCart(cart)
      ) {
        await GiftCart.addToCart(freeGiftVariantId, 1);
        localStorage.setItem('ADD_FIRST_ORDER_GIFT_FREE', Date.now().toString());
        isProcessingCheckout = false;
        setOrderComplete();
        proceedToCheckout(checkoutUrl);
        return;
      }
    }

    isProcessingCheckout = false;
    setOrderComplete();
    proceedToCheckout(checkoutUrl);
  } catch (error) {
    console.error('Error validating cart:', error);
    if (log) log.collapse({ error }, 'Error validating cart, proceeding anyway', 'error');
    isProcessingCheckout = false;
    setOrderComplete();
    proceedToCheckout(checkoutUrl);
  }
}

function handleCheckoutTransition(event, checkoutUrl) {
  const isFirstOrder = localStorage.getItem('isFirstOrder') !== 'false';
  const firstOrderTime = localStorage.getItem('firstOrderTime') || Date.now();
  const fiveMinutesInMs = 5 * 60 * 1000;

  if (
    (!isFirstOrder && Number(Date.now()) - Number(firstOrderTime) < fiveMinutesInMs) ||
    isFirstOrder
  ) {
    if (event) event.preventDefault();
    if (window.freeGiftManager?.enabled) {
      validateAndCleanCart(checkoutUrl);
    } else {
      setOrderComplete();
      proceedToCheckout(checkoutUrl);
    }
  } else if (checkoutUrl && event) {
    event.preventDefault();
    validateAndCleanCart(checkoutUrl);
  }
}

function initCheckoutListeners() {
  const debouncedHandleCheckoutTransition = debounce(handleCheckoutTransition, 800);

  document.addEventListener('click', (event) => {
    const target = event.target.closest('a[href*="/checkout"], a[href$="/checkout"]');
    if (target) {
      event.preventDefault();
      event.stopPropagation();
      debouncedHandleCheckoutTransition(event, target.href);
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (target) {
      event.preventDefault();
      event.stopPropagation();
      debouncedHandleCheckoutTransition(event);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (
      form.tagName === 'FORM' &&
      (form.action.includes('/checkout') ||
        form.querySelector('button[name="checkout"], input[name="checkout"]'))
    ) {
      event.preventDefault();
      event.stopPropagation();
      debouncedHandleCheckoutTransition(event);
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest(
      '.dynamic-checkout__content button, .additional-checkout-buttons button, [data-shopify-buttoncontainer] button'
    );
    if (target) {
      setTimeout(() => {
        event.preventDefault();
        event.stopPropagation();
        debouncedHandleCheckoutTransition(null);
      }, 10);
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest(
      '.cart_checkout_btn, .cart__checkout-button, #checkout, #CartDrawer-Checkout, #cd__checkout-btn'
    );
    if (target) {
      const href = target.href || target.getAttribute('href');
      event.preventDefault();
      event.stopPropagation();
      debouncedHandleCheckoutTransition(event, href);
    }
  });

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function pushState(state, title, url) {
    if (url && url.includes('/checkout')) {
      debouncedHandleCheckoutTransition(null, url);
      return;
    }
    return originalPushState.apply(history, arguments);
  };

  history.replaceState = function replaceState(state, title, url) {
    if (url && url.includes('/checkout')) {
      debouncedHandleCheckoutTransition(null, url);
      return;
    }
    return originalReplaceState.apply(history, arguments);
  };
}

window.addFirstOrderGift = addFirstOrderGift;
window.initGiftCheckoutListeners = initCheckoutListeners;
